import React, { useState, useEffect, useRef } from 'react';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '../../atoms/Badge';
import { Checkbox } from '../../atoms/Checkbox';
import { Skeleton } from '../../atoms/Skeleton';
import type { Case } from '@lnr-v2/types';
import styles from './CasesTable.module.css';

export interface CasesTableProps {
  cases: Case[];
  loading?: boolean;
  selectedIds: string[];
  onSelectCase: (id: string) => void;
  onSelectAll: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  onSort?: (column: string) => void;
  onReorder?: (updates: Array<{ id: string; sortOrder: number }>) => void;
  reorderError?: boolean;
  onReorderErrorHandled?: () => void;
}

const STATUS_BADGE_MAP: Record<
  string,
  'draft' | 'published' | 'archived' | 'warning'
> = {
  DRAFT: 'draft',
  IN_PROGRESS: 'warning',
  COMPLETED: 'published',
  ARCHIVED: 'archived',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Publié',
  ARCHIVED: 'Archivé',
};

interface SortableRowProps {
  caseItem: Case;
  selectedIds: string[];
  onSelectCase: (id: string) => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  formatDate: (date: string) => string;
  isDraggable: boolean;
}

const SortableRow: React.FC<SortableRowProps> = ({
  caseItem,
  selectedIds,
  onSelectCase,
  onView,
  onEdit,
  onDelete,
  formatDate,
  isDraggable,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: caseItem.id, disabled: !isDraggable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${selectedIds.includes(caseItem.id) ? styles.selected : ''} ${isDragging ? styles.dragging : ''}`}
    >
      <td>
        {isDraggable && (
          <button
            className={styles.dragHandle}
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm5-8a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
            </svg>
          </button>
        )}
      </td>
      <td>
        <Checkbox
          checked={selectedIds.includes(caseItem.id)}
          onChange={() => onSelectCase(caseItem.id)}
          aria-label={`Select ${caseItem.name}`}
        />
      </td>
      <td className={styles.positionCell}>{caseItem.displayOrder}</td>
      <td>
        {caseItem.cover || caseItem.thumbnailImage ? (
          <img
            src={caseItem.cover || caseItem.thumbnailImage}
            alt={caseItem.name}
            className={styles.thumbnail}
            loading="lazy"
          />
        ) : (
          <div className={styles.thumbnailPlaceholder}>
            <span>—</span>
          </div>
        )}
      </td>
      <td>
        <div className={styles.caseDetails}>
          <button
            className={styles.caseTitle}
            onClick={() => onView(caseItem.id)}
          >
            {caseItem.name}
          </button>
          {caseItem.slogan && (
            <span className={styles.caseSlogan}>{caseItem.slogan}</span>
          )}
          {(caseItem.quickDescription || caseItem.description) && (
            <span className={styles.caseDescription}>
              {caseItem.quickDescription || caseItem.description}
            </span>
          )}
        </div>
      </td>
      <td>{caseItem.clientName}</td>
      <td>{caseItem.category?.name || '—'}</td>
      <td>
        {caseItem.status && (
          <Badge variant={STATUS_BADGE_MAP[caseItem.status] || 'neutral'}>
            {STATUS_LABELS[caseItem.status] || caseItem.status}
          </Badge>
        )}
      </td>
      <td className={styles.centerAlign}>
        {caseItem.isFeatured && (
          <span className={styles.featuredBadge}>Featured</span>
        )}
      </td>
      <td>
        <div className={styles.tags}>
          {caseItem.tags.slice(0, 2).map((tag) => (
            <span key={tag} className={styles.tag}>
              #{tag}
            </span>
          ))}
          {caseItem.tags.length > 2 && (
            <span className={styles.moreTag}>+{caseItem.tags.length - 2}</span>
          )}
        </div>
      </td>
      <td className={styles.dateColumn}>
        {caseItem.updatedAt ? formatDate(caseItem.updatedAt) : '—'}
      </td>
      <td>
        <div className={styles.actions}>
          <button
            className={styles.actionButton}
            onClick={() => onEdit(caseItem.id)}
            title="Edit"
          >
            Edit
          </button>
          <button
            className={`${styles.actionButton} ${styles.delete}`}
            onClick={() => onDelete(caseItem.id)}
            title="Delete"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
};

export const CasesTable: React.FC<CasesTableProps> = ({
  cases,
  loading = false,
  selectedIds,
  onSelectCase,
  onSelectAll,
  onEdit,
  onDelete,
  onView,
  sortBy,
  sortOrder = 'DESC',
  onSort,
  onReorder,
  reorderError,
  onReorderErrorHandled,
}) => {
  const [localCases, setLocalCases] = useState<Case[]>(cases);
  const originalOrderRef = useRef<Case[] | null>(null);
  const allSelected = cases.length > 0 && selectedIds.length === cases.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < cases.length;

  // Sync local state with props
  useEffect(() => {
    setLocalCases(cases);
  }, [cases]);

  // Rollback on reorder error
  useEffect(() => {
    if (reorderError && originalOrderRef.current) {
      setLocalCases(originalOrderRef.current);
      originalOrderRef.current = null;
      onReorderErrorHandled?.();
    }
  }, [reorderError, onReorderErrorHandled]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (_event: DragStartEvent) => {
    // Snapshot original order for potential rollback
    originalOrderRef.current = [...localCases];
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localCases.findIndex((c) => c.id === active.id);
      const newIndex = localCases.findIndex((c) => c.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newCases = arrayMove(localCases, oldIndex, newIndex);
        setLocalCases(newCases);

        // Calculate new sort orders and call onReorder
        if (onReorder) {
          const updates = newCases.map((c, index) => ({
            id: c.id,
            sortOrder: index + 1,
          }));
          onReorder(updates);
        }
      }
    } else {
      // Drag cancelled or dropped in same position, clear snapshot
      originalOrderRef.current = null;
    }
  };

  const handleSort = (column: string) => {
    if (onSort) {
      onSort(column);
    }
  };

  const renderSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <span className={styles.sortIcon}>↕</span>;
    }

    return sortOrder === 'ASC' ? (
      <span className={styles.sortIcon}>↑</span>
    ) : (
      <span className={styles.sortIcon}>↓</span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className={styles.table}>
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <Skeleton width="20px" height="20px" />
              </th>
              <th style={{ width: '40px' }}>
                <Skeleton width="20px" height="20px" />
              </th>
              <th style={{ width: '50px' }}>
                <Skeleton width="30px" />
              </th>
              <th style={{ width: '80px' }}>
                <Skeleton width="60px" />
              </th>
              <th>
                <Skeleton width="100px" />
              </th>
              <th>
                <Skeleton width="100px" />
              </th>
              <th>
                <Skeleton width="80px" />
              </th>
              <th>
                <Skeleton width="60px" />
              </th>
              <th>
                <Skeleton width="60px" />
              </th>
              <th>
                <Skeleton width="100px" />
              </th>
              <th>
                <Skeleton width="80px" />
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }).map((_, i) => (
              <tr key={i}>
                <td>
                  <Skeleton width="20px" height="20px" />
                </td>
                <td>
                  <Skeleton width="20px" height="20px" />
                </td>
                <td>
                  <Skeleton width="30px" />
                </td>
                <td>
                  <Skeleton width="60px" height="40px" />
                </td>
                <td>
                  <Skeleton width="150px" />
                </td>
                <td>
                  <Skeleton width="120px" />
                </td>
                <td>
                  <Skeleton width="80px" />
                </td>
                <td>
                  <Skeleton width="60px" />
                </td>
                <td>
                  <Skeleton width="60px" />
                </td>
                <td>
                  <Skeleton width="100px" />
                </td>
                <td>
                  <Skeleton width="80px" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>📁</div>
        <h3>No cases found</h3>
        <p>Get started by creating your first case</p>
      </div>
    );
  }

  const isDraggable = !!onReorder;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {isDraggable && <th className={styles.dragColumn}></th>}
              <th className={styles.checkboxColumn}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={onSelectAll}
                  aria-label="Select all cases"
                />
              </th>
              <th
                className={`${styles.sortable} ${styles.positionColumn}`}
                onClick={() => handleSort('displayOrder')}
              >
                <div className={styles.headerContent}>
                  #{onSort && renderSortIcon('displayOrder')}
                </div>
              </th>
              <th className={styles.imageColumn}>Cover</th>
              <th
                className={styles.sortable}
                onClick={() => handleSort('name')}
              >
                <div className={styles.headerContent}>
                  Name
                  {onSort && renderSortIcon('name')}
                </div>
              </th>
              <th
                className={styles.sortable}
                onClick={() => handleSort('clientName')}
              >
                <div className={styles.headerContent}>
                  Client
                  {onSort && renderSortIcon('clientName')}
                </div>
              </th>
              <th>Category</th>
              <th>Statut</th>
              <th className={styles.centerAlign}>Featured</th>
              <th>Tags</th>
              <th
                className={styles.sortable}
                onClick={() => handleSort('updatedAt')}
              >
                <div className={styles.headerContent}>
                  Updated
                  {onSort && renderSortIcon('updatedAt')}
                </div>
              </th>
              <th className={styles.actionsColumn}>Actions</th>
            </tr>
          </thead>
          <SortableContext
            items={localCases.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {localCases.map((caseItem) => (
                <SortableRow
                  key={caseItem.id}
                  caseItem={caseItem}
                  selectedIds={selectedIds}
                  onSelectCase={onSelectCase}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  formatDate={formatDate}
                  isDraggable={isDraggable}
                />
              ))}
            </tbody>
          </SortableContext>
        </table>
      </div>
    </DndContext>
  );
};
